import { Entity } from '../../entity';
import { DayShortName } from '../../shared/schedule.entity';
import { FlattenType } from '../../../types/flatten.type';
// Type-only: `entity.view.ts` imports EntityViewCalendar back, so a value import here would close a
// runtime cycle. `import type` is erased, keeping the pair purely structural.
import type { EntityViewCard } from './entity.view';
import { EntityCalendarOrientation, EntityCalendarResourceType, EntityCalendarView } from './enums';

/**
 * Attribute OF THE RESOURCE REF the lanes group under, not a field of the record. `'type'` reads
 * `record[resources.field].type` (e.g. `resource.type` → Employee / Contractor), so one parent lane
 * is emitted per distinct value and every lane sourced from that value hangs under it.
 *
 * A closed set rather than a free path: lane grouping only makes sense for attributes the resource
 * ref actually carries, and widening it to arbitrary paths would let a view name something the
 * derivation cannot title. A roster whose refs carry none of it stays flat rather than nesting
 * every lane under one catch-all header.
 */
export const EntityCalendarResourceGroupBy = {
    /** `record[resources.field].type` — Employee / Contractor / Equipment. */
    Type: 'type',
} as const;

export type EntityCalendarResourceGroupBy = typeof EntityCalendarResourceGroupBy[keyof typeof EntityCalendarResourceGroupBy];

/**
 * Per resource-type lane policy. Policy only: which lane types show and which work categories
 * restrict them. The roster itself is derived from data at runtime and never persisted on the view
 * (the legacy `AllocationViewResourcesConstraints.resources` roster is deliberately not carried).
 */
export interface EntityViewCalendarResourceLane {
    show: boolean;
    /**
     * Work categories restricting which resources of this type appear. Absent = all.
     *
     * Reserved for the resource loader: narrowing by work category needs category data that hangs
     * off the RESOURCE, not off the records the board fetched, so lane derivation cannot honour it.
     * It is handed to `EntityCalendarResourceLoader` in its context so an entity that can ask the
     * server for its roster can apply it; the derivation fallback ignores it rather than guessing.
     */
    restrictTo?: Entity[];
}

/** A saved roster restriction. Empty ids means no selected resources, never all resources. */
export interface EntityCalendarResourceSelection {
    mode: 'named' | 'teams';
    /** Resource contact ids, or team ids whose current members supply the roster. */
    ids: string[];
}

/** Resource axis configuration. Used only by resource-based calendar layouts. */
export interface EntityViewCalendarResources<T = any> {
    /**
     * Record field naming the resource(s) an event belongs to — the thing work is booked AGAINST for
     * this entity, which is not always a person: a Visit board lanes by plan or by line, a line board
     * by task, a scheduler by employee, contractor or equipment. Any groupable lookup qualifies.
     */
    field: FlattenType<T>;
    /** Absent = all eligible resources. Team membership is resolved when the roster loads. */
    selection?: EntityCalendarResourceSelection;
    /**
     * Lane policy per resource type. Absent map, or an absent entry for a type, means SHOWN — only an
     * entry that explicitly says `show: false` hides anything.
     *
     * Parity policy for CONTACT-shaped lanes, and a no-op for lane sources whose refs carry no
     * mappable `type` (plans, lines, tasks): those lanes have no resource type to state policy about,
     * so they are always shown regardless of what this map holds.
     */
    lanes: Partial<Record<EntityCalendarResourceType, EntityViewCalendarResourceLane>>;
    /**
     * Group lanes under a parent lane per distinct value of this attribute of the resource ref.
     * Absent = a flat lane list. See {@link EntityCalendarResourceGroupBy}.
     */
    groupBy?: EntityCalendarResourceGroupBy;
    /** Only the current user's lane (legacy `mySchedule`). */
    mineOnly?: boolean;
    showAvatar?: boolean;
    showSuburb?: boolean;
    showSchedulingTags?: boolean;
    showTimezone?: boolean;
    showType?: boolean;
}

/**
 * Serialisable Calendar display configuration for a saved view (`EntityView.calendar`).
 * FullCalendar-independent: no component types, callbacks, or raw CalendarOptions. Runtime state
 * (current date/range, current view, selected resources, scroll) lives on the board, not here.
 */
export interface EntityViewCalendar<T = any> {
    _id: string;

    /** Layout the view opens in. */
    view: EntityCalendarView;
    /** Layouts the user may switch between at runtime. Absent = manifest `board.calendar.supportedViews`. */
    views?: EntityCalendarView[];

    /** Record field giving the event start (date or date-time). */
    startField: FlattenType<T>;
    /** Record field giving the event end. Absent = point / all-day event. */
    endField?: FlattenType<T>;
    /** Render events as all-day blocks rather than timed slots. */
    allDay: boolean;

    /**
     * Fields printed on the event, in the same slot shape Card and Kanban use, so one card field
     * picker configures all three displays. The calendar's default renderer is deliberately terser
     * than the Kanban card: dense layouts print the title and status only, roomier ones add `body`.
     */
    card: EntityViewCard<T>;
    /** Field driving event colour (status, priority, job type, ...). Absent = entity default. */
    colorField?: FlattenType<T>;

    resources?: EntityViewCalendarResources<T>;

    /** Days shown by week-style layouts (legacy `showDays`, 1..7). Absent = full week. */
    showDays?: number;
    daysOfWeek?: DayShortName[];
    /** `HH:mm` visible-hours bounds for timed layouts. */
    slotMinTime?: string;
    slotMaxTime?: string;
    /** Slot granularity in minutes for timeline layouts. */
    slotDuration?: number;
    /** IANA timezone; absent = viewer's. */
    timezone?: string;
    orientation?: EntityCalendarOrientation;

    /** Auto refresh interval in minutes. Absent/0 = off. */
    autoRefresh?: number;
}

/** Visible range the calendar is showing, forwarded on items requests as `range` (ISO date-times, end exclusive). */
export interface EntityItemsRange {
    start: string;
    end: string;
}
